import { Suspense, lazy, useContext, useLayoutEffect, useMemo } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Box, ThemeProvider } from "@mui/material";
import PwaAppBar from "../components/pwa/PwaAppBar";
import PwaBottomBar from "../components/PwaBottomBar";
import PwaRadioMiniPlayer from "../components/pwa/PwaRadioMiniPlayer";
import { AiChatProvider } from "../context/AiChatContext";
import { RadioPlaybackProvider } from "../context/RadioPlaybackContext";
import { useDisplayMode } from "../context/DisplayModeContext";
import { PwaChromeContext, usePwaChrome } from "../context/PwaChromeContext";
import { AppLayoutContext } from "../../contexts/AppLayoutContext";
import { applyNavHeight } from "./navMetrics";
import { PWA_HEADER, PWA_LAYOUT, isPwaAnonymousRoute, pwaRouteSegment } from "./pwaConfig";
import generateRewampTheme from "../theme";

const RewampAiChatWidget = lazy(() => import("../components/AiChat/RewampAiChatWidget"));

export { usePwaChrome };

export default function PwaShellLayout() {
  const { ourTheme, fontSize } = useContext(AppLayoutContext);
  const { standalone } = useDisplayMode();
  const { pathname } = useLocation();
  const segment = pwaRouteSegment(pathname);
  const signedIn = standalone && !isPwaAnonymousRoute(pathname);
  const toolBar = PWA_HEADER.toolBarRoutes.includes(segment);
  const showWelcome = signedIn && !PWA_HEADER.welcomeHiddenRoutes.includes(segment);
  const showAppBar = signedIn && (showWelcome || toolBar);

  const chrome = useMemo(() => ({ header: showAppBar, bottomBar: signedIn }), [showAppBar, signedIn]);
  const rewampTheme = useMemo(() => generateRewampTheme(fontSize, ourTheme), [fontSize, ourTheme]);

  // With the header shown, PwaAppBar writes its measured height instead.
  useLayoutEffect(() => (standalone && !signedIn ? applyNavHeight("none") : undefined), [standalone, signedIn]);

  if (!standalone) return <Outlet />;

  return (
    <PwaChromeContext.Provider value={chrome}>
      <AiChatProvider>
        <RadioPlaybackProvider>
          {showAppBar && (
            <ThemeProvider theme={rewampTheme}>
              {/* A sibling spacer, not a wrapper, so the sticky app bar keeps the page as its scroll container. */}
              <Box aria-hidden="true" sx={{ pt: PWA_LAYOUT.topInset, backgroundColor: "background.tint" }} />
              <PwaAppBar toolBar={toolBar} showWelcome={showWelcome} />
            </ThemeProvider>
          )}
          <Outlet />
          {signedIn && (
            <ThemeProvider theme={rewampTheme}>
              <Box aria-hidden="true" sx={{ height: PWA_LAYOUT.bottomClearance, backgroundColor: "background.tint" }} />
              <PwaRadioMiniPlayer />
              <PwaBottomBar />
              <Suspense fallback={null}>
                <RewampAiChatWidget showLauncher={false} />
              </Suspense>
            </ThemeProvider>
          )}
        </RadioPlaybackProvider>
      </AiChatProvider>
    </PwaChromeContext.Provider>
  );
}
